import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Modal, Select } from 'antd';
import { UploadedPackagesVisualStore } from '../../stores';

const { Option } = Select;

type Props = {
    isVisible: boolean;
    setIsVisible: (isVisible: boolean) => void;
    store: UploadedPackagesVisualStore;
};

const ManagePackageTagsModal: React.FC<Props> = ({ isVisible, setIsVisible, store }) => {
    const [tagsToAdd, setTagsToAdd] = React.useState<string[]>([]);
    const [tagsToRemove, setTagsToRemove] = React.useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const handleAddTagsChange = (value: string[]) => {
        setTagsToAdd(value);
    };

    const handleRemoveTagsChange = (value: string[]) => {
        setTagsToRemove(value);
    };

    React.useEffect(() => {
        if (!isVisible) {
            return;
        }

        setTagsToAdd([]);
        setTagsToRemove([]);
    }, [isVisible]);

    const handleClose = () => {
        if (isSubmitting) {
            return;
        }

        setIsVisible(false);
    };

    const handleApply = async () => {
        setIsSubmitting(true);

        try {
            const updated = await store.updateSelectedPackagesTags(tagsToAdd, tagsToRemove);

            if (updated) {
                setIsVisible(false);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            className="alpha-portal-dialog"
            title="Manage tags"
            visible={isVisible}
            onCancel={handleClose}
            maskClosable={false}
            destroyOnClose
            centered
            footer={[
                <Button key="cancel" size="large" className="light" onClick={handleClose} disabled={isSubmitting}>
                    Cancel
                </Button>,
                <Button
                    key="apply"
                    size="large"
                    type="primary"
                    onClick={handleApply}
                    loading={isSubmitting}
                    disabled={!tagsToAdd.length && !tagsToRemove.length}
                >
                    Apply
                </Button>
            ]}
        >
            <div style={{ marginBottom: 16 }}>{`${store.selectedPackages.length} documents selected`}</div>
            <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>Add tags</div>
                <Select
                    style={{ width: '100%' }}
                    mode="tags"
                    value={tagsToAdd}
                    onChange={handleAddTagsChange}
                    placeholder="Choose or create tags"
                    data-id="manage-tags-add-select"
                >
                    {store.tags.map((tag, index) => (
                        <Option key={`add-tag-${index}`} value={tag}>
                            {tag}
                        </Option>
                    ))}
                </Select>
            </div>
            <div>
                <div style={{ marginBottom: 8 }}>Remove tags</div>
                <Select
                    style={{ width: '100%' }}
                    mode="multiple"
                    value={tagsToRemove}
                    onChange={handleRemoveTagsChange}
                    placeholder={
                        store.selectedPackageTags.length ? 'Choose tags to remove' : 'No tags available to remove'
                    }
                    data-id="manage-tags-remove-select"
                    disabled={!store.selectedPackageTags.length}
                >
                    {store.selectedPackageTags.map(tag => (
                        <Option key={`remove-tag-${tag}`} value={tag}>
                            {tag}
                        </Option>
                    ))}
                </Select>
            </div>
        </Modal>
    );
};

export default observer(ManagePackageTagsModal);
